<template>
  <v-card class="pa-4">
    <div class="d-flex align-center justify-space-between ga-2">
      <div class="text-subtitle-1 ncrm-record-title">
        {{ title }}
      </div>
      <v-btn
        v-if="showSettingsButton"
        icon="mdi-cog-outline"
        variant="text"
        size="small"
        color="primary"
        :disabled="settingsDisabled"
        :aria-label="settingsAriaLabel || ''"
        @click="$emit('settings-click')"
      />
    </div>

    <div v-if="loading" class="ncrm-property-skeleton mt-4" aria-hidden="true">
      <div
        v-for="(rowsCount, sectionIndex) in normalizedSkeletonGroups"
        :key="`property-skeleton-${sectionIndex}`"
        class="ncrm-property-skeleton__group"
      >
        <v-skeleton-loader type="text" class="ncrm-property-skeleton__title" />
        <div class="ncrm-property-skeleton__rows">
          <div
            v-for="rowIndex in rowsCount"
            :key="`property-skeleton-row-${sectionIndex}-${rowIndex}`"
            class="ncrm-property-skeleton__row"
          >
            <v-skeleton-loader type="text" class="ncrm-property-skeleton__label" />
            <v-skeleton-loader type="text" class="ncrm-property-skeleton__value" />
          </div>
        </div>
      </div>
    </div>

    <div v-else class="ncrm-property-sections mt-3">
      <template v-if="normalizedSections.length">
        <section
          v-for="section in normalizedSections"
          :key="`property-section-${section.key}`"
          class="ncrm-property-sections__group"
        >
          <div class="text-caption text-medium-emphasis ncrm-property-sections__title">
            {{ section.label }}
          </div>
          <div class="ncrm-details-list ncrm-property-sections__list">
            <template v-if="section.rows.length">
              <div
                v-for="row in section.rows"
                :key="`property-row-${section.key}-${row.key}`"
                class="ncrm-details-list__item"
              >
                <!-- GDPR-122: starea Protecția datelor stă pe rândul etichetei, nu pe un rând separat. -->
                <!-- Chipul stă lângă etichetă, nu în ea: nu moștenește stilul caption; rândurile fără chip rămân neschimbate. -->
                <div v-if="privacyStateBySlot[row.key]" class="d-flex align-center flex-wrap ga-2">
                  <span class="text-caption text-medium-emphasis">{{ row.label }}</span>
                  <NcrmPrivacyStatusChip :state="privacyStateBySlot[row.key].state" size="x-small" />
                </div>
                <div v-else class="text-caption text-medium-emphasis">{{ row.label }}</div>
                <div class="text-body-2 ncrm-details-list__value ncrm-details-list__value--with-actions">
                  <template v-if="resolveRowLinkMeta(row)">
                    <NcrmPrivacyPhoneLink
                      v-if="resolveRowLinkMeta(row).type === 'phone' && hasPrivacySubject"
                      class="ncrm-details-list__link"
                      :phone="String(row.value || '')"
                      :href-value="resolveRowLinkMeta(row).href"
                    >
                      {{ row.value }}
                    </NcrmPrivacyPhoneLink>
                    <a
                      v-else
                      class="ncrm-details-list__link"
                      :href="resolveRowLinkMeta(row).href"
                      :target="resolveRowLinkMeta(row).target || undefined"
                      :rel="resolveRowLinkMeta(row).rel || undefined"
                    >
                      {{ row.value }}
                    </a>
                  </template>
                  <template v-else>
                    <v-tooltip v-if="resolveRowTooltipValue(row)" location="top">
                      <template #activator="{ props }">
                        <span v-bind="props" class="ncrm-details-list__value-content">{{ row.value }}</span>
                      </template>
                      <span>{{ resolveRowTooltipValue(row) }}</span>
                    </v-tooltip>
                    <span v-else class="ncrm-details-list__value-content">{{ row.value }}</span>
                  </template>

                  <v-tooltip v-if="isRowCopyable(row)" location="top">
                    <template #activator="{ props }">
                      <v-btn
                        v-bind="props"
                        icon="mdi-content-copy"
                        size="x-small"
                        variant="text"
                        color="primary"
                        :aria-label="copyAriaLabel"
                        @click.stop="$emit('copy-row', row)"
                      />
                    </template>
                    <span>{{ copyAriaLabel }}</span>
                  </v-tooltip>
                </div>
              </div>
            </template>

            <div v-else class="ncrm-property-sections__empty">
              <span class="text-body-2 text-medium-emphasis">
                {{ emptyLabel }}
              </span>
              <v-btn
                v-if="section.emptyAction"
                size="x-small"
                variant="text"
                density="compact"
                color="primary"
                class="ncrm-property-sections__empty-action"
                @click="$emit('empty-action', section.emptyAction)"
              >
                {{ section.emptyActionLabel || section.emptyAction }}
              </v-btn>
            </div>
          </div>
        </section>
      </template>

      <div v-else class="text-body-2 text-medium-emphasis">
        {{ emptySectionsLabel || emptyLabel }}
      </div>
    </div>
  </v-card>
</template>

<script>
import NcrmPrivacyPhoneLink from "@/components/ncrm/shared/NcrmPrivacyPhoneLink.vue";
import NcrmPrivacyStatusChip from "@/components/ncrm/shared/NcrmPrivacyStatusChip.vue";
import {
  CONTACT_SLOT_PATTERN, fetchContactabilityByKey, privacyContactabilityChanges,
} from "@/Utile/ncrmPrivacyContactability.mjs";

export default {
  name: "NcrmRecordPropertiesCard",
  components: { NcrmPrivacyPhoneLink, NcrmPrivacyStatusChip },
  props: {
    title: {
      type: String,
      default: "",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    sections: {
      type: Array,
      default: () => [],
    },
    skeletonGroups: {
      type: Array,
      default: () => [3, 2, 3],
    },
    emptyLabel: {
      type: String,
      default: "—",
    },
    emptySectionsLabel: {
      type: String,
      default: "",
    },
    showSettingsButton: {
      type: Boolean,
      default: false,
    },
    settingsDisabled: {
      type: Boolean,
      default: false,
    },
    settingsAriaLabel: {
      type: String,
      default: "",
    },
    copyAriaLabel: {
      type: String,
      default: "",
    },
    resolveRowLink: {
      type: Function,
      default: null,
    },
    resolveRowTooltip: {
      type: Function,
      default: null,
    },
    canCopyRow: {
      type: Function,
      default: null,
    },
    // GDPR S9: `{ type: "person" | "company", id }` afișează starea Protecția datelor lângă email/telefon, iar
    // telefonul trece prin `NcrmPrivacyPhoneLink`, care verifică numărul la fiecare click (GDPR-122).
    privacySubject: {
      type: Object,
      default: null,
    },
  },
  emits: ["settings-click", "copy-row", "empty-action"],
  data() {
    return { privacyStateBySlot: {}, privacyRequest: 0, privacyLoadedSubject: "" };
  },
  computed: {
    normalizedSections() {
      return Array.isArray(this.sections) ? this.sections : [];
    },
    hasPrivacySubject() {
      return Boolean(this.privacySubject?.type && this.privacySubject?.id);
    },
    // Valorile email/telefon afișate: starea se citește exact pentru ele (nu pe slot), deci o editare sau un slot rescris
    // de alt operator nu lasă chipul altui număr lângă valoarea de pe ecran.
    privacyContactValues() {
      return this.normalizedSections.flatMap((section) => section?.rows || [])
        .filter((row) => CONTACT_SLOT_PATTERN.test(String(row?.key || "")) && String(row?.value ?? "").trim())
        .map((row) => ({ key: row.key, type: row.key.startsWith("email") ? "email" : "phone", value: String(row.value) }));
    },
    privacySubjectKey() {
      const subject = this.privacySubject;
      if (!this.hasPrivacySubject) return "";
      const contacts = this.privacyContactValues.map((entry) => `${entry.key}=${entry.value}`);
      return [subject.type, subject.id, privacyContactabilityChanges.version, ...contacts].join(":");
    },
    normalizedSkeletonGroups() {
      return Array.isArray(this.skeletonGroups) && this.skeletonGroups.length
        ? this.skeletonGroups
        : [3, 2, 3];
    },
  },
  watch: {
    privacySubjectKey: { immediate: true, handler: "loadPrivacyStates" },
  },
  methods: {
    async loadPrivacyStates() {
      const request = ++this.privacyRequest;
      const subject = this.hasPrivacySubject ? `${this.privacySubject.type}:${this.privacySubject.id}` : "";
      // Alt record: chipurile vechi dispar imediat, nu rămân lângă valorile noului record până la răspuns.
      if (subject !== this.privacyLoadedSubject) { this.privacyStateBySlot = {}; this.privacyLoadedSubject = subject; }
      if (!subject) { this.privacyStateBySlot = {}; return; }
      try {
        const states = await fetchContactabilityByKey(this.privacyContactValues);
        if (request === this.privacyRequest) this.privacyStateBySlot = states;
      } catch {
        if (request === this.privacyRequest) this.privacyStateBySlot = {};
      }
    },
    resolveRowLinkMeta(row) {
      if (typeof this.resolveRowLink !== "function") return null;
      return this.resolveRowLink(row);
    },
    resolveRowTooltipValue(row) {
      if (typeof this.resolveRowTooltip !== "function") return "";
      return this.resolveRowTooltip(row?.key || "", row);
    },
    isRowCopyable(row) {
      if (typeof this.canCopyRow !== "function") return false;
      return this.canCopyRow(row) === true;
    },
  },
};
</script>

<style scoped>
.ncrm-record-title {
  color: var(--ncrm-color-text-strong);
}

.ncrm-details-list {
  display: grid;
  gap: 3px;
}

.ncrm-details-list__item {
  border-bottom: 1px solid var(--ncrm-color-border-default);
  padding-bottom: 3px;
}

.ncrm-details-list__item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.ncrm-details-list__value {
  color: var(--ncrm-color-text-strong);
  word-break: break-word;
  line-height: 1.25;
}

.ncrm-details-list__value--with-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  line-height: 1.25;
}

.ncrm-details-list__value--with-actions :deep(.v-btn) {
  min-width: 0 !important;
  min-height: 0 !important;
  width: 16px !important;
  height: 16px !important;
  padding: 0 !important;
}

.ncrm-details-list__value--with-actions :deep(.v-btn .v-icon) {
  font-size: 13px !important;
}

.ncrm-details-list__value-content {
  min-width: 0;
}

.ncrm-details-list__link {
  color: inherit;
  text-decoration: none;
}

.ncrm-details-list__link:hover {
  text-decoration: underline;
}

.ncrm-property-sections {
  display: grid;
  gap: 0;
}

.ncrm-property-sections__group + .ncrm-property-sections__group {
  border-top: 1px solid var(--ncrm-color-border-subtle);
  margin-top: 7px;
  padding-top: 7px;
}

.ncrm-property-skeleton {
  display: grid;
  gap: 16px;
}

.ncrm-property-skeleton__group {
  display: grid;
  gap: 8px;
}

.ncrm-property-skeleton__title {
  max-width: 120px;
}

.ncrm-property-skeleton__rows {
  display: grid;
  gap: 12px;
}

.ncrm-property-skeleton__row {
  display: grid;
  gap: 4px;
}

.ncrm-property-skeleton__label {
  max-width: 140px;
}

.ncrm-property-skeleton__value {
  max-width: 100%;
}

.ncrm-property-sections__title {
  letter-spacing: 0.4px;
  text-transform: none;
  font-weight: 600;
}

.ncrm-property-sections__empty {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 0 4px;
  min-height: 20px;
}

.ncrm-property-sections__empty-action {
  min-width: 0;
  min-height: 0;
  padding: 0 4px;
  font-size: 12px;
  text-transform: none;
  letter-spacing: 0;
}
</style>
